import { TableSlice, ArenaStoreState } from '../arenaStoreTypes';
import { adaptTableState } from '../../state/stateAdapter';
import { Card, ArenaGameState, SeatPosition } from '../../state/ArenaTypes';
import { normalizeGameTableConfig } from '../../engine/GameTableConfig';

export type StoreSet = (
  partial: Partial<ArenaStoreState> | ((state: ArenaStoreState) => Partial<ArenaStoreState>)
) => void;
export type StoreGet = () => ArenaStoreState;

export const initialTableState = {
  tableId: null as string | null,
  privateHandTableId: null as string | null,
  gameState: null as ArenaGameState | null,
  authoritativeBaseState: null as ArenaGameState | null,
  effectiveGameState: null as ArenaGameState | null,
  playableCardIds: [] as string[],
  canClaim: false as boolean,
  canGiveUp: false as boolean,
  mySeat: null as SeatPosition | null,
  authoritativeMySeat: null as SeatPosition | null,
  myPrivateHand: [] as Card[],
  effectivePrivateHand: [] as Card[],
  kittyCardIds: [] as string[],
  passedReceivedCardIds: [] as string[],
  isHandVerified: true,
  isHandLoading: false,
  loading: true,
  gameConfig: null,

  // Identity
  myPlayerId: null as string | null,
  myName: 'Card Shark',
  myPhotoUrl: null as string | null,
  isTableCreatorOrHost: false,
};

export const createTableSlice = (
  set: StoreSet,
  get: StoreGet,
  triggerRecompute: () => void,
  initialTableId: string | null = null
): TableSlice => ({
  ...initialTableState,
  tableId: initialTableId,

  setTableId: (tableId) => {
    set((state) => {
      if (state.tableId !== tableId) {
        return {
          tableId,
          privateHandTableId: null,
          gameState: null,
          authoritativeBaseState: null,
          effectiveGameState: null,
          playableCardIds: [],
          canClaim: false,
          canGiveUp: false,
          mySeat: null,
          authoritativeMySeat: null,
          myPrivateHand: [],
          effectivePrivateHand: [],
          kittyCardIds: [],
          passedReceivedCardIds: [],
          isHandVerified: true,
          isHandLoading: false,
          loading: true,
          isResyncing: false,
          resyncPhase: 'idle' as const,
          resyncMessage: 'Syncing table state...',
          resyncSubMessage: undefined,
          lastStateReceivedTime: 0,
          lastLocalPlayedCardId: null,
          gameConfig: null,

          // Optimistic State
          optimisticSeat: null,
          optimisticReady: null,
          optimisticBid: null,
          optimisticTrump: null,
          optimisticDiscards: null,
          optimisticPlay: null,

          // In-Flight Locks
          isActionPending: false,
          remotePendingAction: null,
          isBidInFlight: false,
          bidStartTime: 0,
          isPlayInFlight: false,
          playStartTime: 0,
          isBotStepInFlight: false,
          botStepStartTime: 0,
          scheduledDealingHand: null,
          hasFinalizedHand: null,
          isDealingAnimationActive: false,
          wasDealingAnimationActive: false,
          isDealingInitiated: false,
          lastAnimatedHand: null,
          activeDealingDealerSeat: null,
          activeDealKey: null,
          activeTrickVisual: null,
          sweptTrickKeys: [],
        };
      }
      return { tableId };
    });
    triggerRecompute();
  },

  setIdentity: (identity) => {
    set((state) => ({
      myPlayerId: identity.myPlayerId !== undefined ? identity.myPlayerId : state.myPlayerId,
      myName: identity.myName !== undefined ? identity.myName : state.myName,
      myPhotoUrl: identity.myPhotoUrl !== undefined ? identity.myPhotoUrl : state.myPhotoUrl,
      isTableCreatorOrHost:
        identity.isTableCreatorOrHost !== undefined ? identity.isTableCreatorOrHost : state.isTableCreatorOrHost,
    }));
    triggerRecompute();
  },

  recomputeDerivedState: () => {
    triggerRecompute();
  },

  setGameState: (updater, targetTableId) => {
    set((state) => {
      const incomingTableId = targetTableId || (typeof updater === 'object' && updater ? (updater as any).tableId : null);
      if (incomingTableId && state.tableId && incomingTableId !== state.tableId) {
        return state;
      }
      if (targetTableId && state.tableId && targetTableId !== state.tableId) {
        return state;
      }
      const rawNextState = typeof updater === 'function' ? updater(state.gameState) : updater;
      const adaptedState = rawNextState ? (adaptTableState(rawNextState) as any) : null;

      if (adaptedState && state.gameState) {
        const curHand = state.gameState.handNumber || 0;
        const nxtHand = adaptedState.handNumber || 0;
        const curSeq = Number((state.gameState as any).actionSeq || state.gameState.revision || 0);
        const nxtSeq = Number((adaptedState as any).actionSeq || adaptedState.revision || 0);

        const isMatchReset = nxtHand <= 1 && (
          adaptedState.phase === 'DEALING' ||
          adaptedState.phase === 'LOBBY' ||
          state.gameState.phase === 'GAME_OVER'
        );

        if (!isMatchReset) {
          if (curHand > 0 && nxtHand < curHand) {
            return state;
          }
          if (curHand === nxtHand && curSeq > 0 && nxtSeq > 0 && nxtSeq < curSeq) {
            return state;
          }
        }
        if (curHand > 0 && nxtHand !== curHand) {
          delete (adaptedState as any).booksToSet;
        }
      }
      if (adaptedState && ((adaptedState as any).phase === 'DEALING' || (adaptedState as any).phase === 'BIDDING' || (adaptedState as any).phase === 'LOBBY')) {
        delete (adaptedState as any).booksToSet;
      }

      const rawNextConfig = (adaptedState as any)?.gameConfig ?? state.gameConfig;
      const nextConfig = rawNextConfig ? normalizeGameTableConfig(rawNextConfig) : null;
      const currentTurn = (adaptedState as any)?.currentTurnSeat;
      const currentPhase = (adaptedState as any)?.phase;
      const isPlayPhase = currentPhase === 'PLAYING' || currentPhase === 'PLAY';
      const isForMySeat = state.mySeat !== null && currentTurn !== undefined && currentTurn === state.mySeat;
      let nextPlayableCardIds = state.playableCardIds;

      const prevCompleted = state.gameState?.completedTricks?.length ?? 0;
      const nextCompleted = adaptedState?.completedTricks?.length ?? 0;
      const prevTrickLen = state.gameState?.currentTrick?.length ?? 0;
      const nextTrickLen = adaptedState?.currentTrick?.length ?? 0;
      const isNewTrick = prevCompleted !== nextCompleted || (nextTrickLen === 0 && prevTrickLen > 0);

      if (!isPlayPhase || (currentTurn !== undefined && state.mySeat !== null && currentTurn !== state.mySeat)) {
        // If not in play phase or turn has moved to another seat, playable cards should be reset
        nextPlayableCardIds = [];
      } else if (isNewTrick && (!adaptedState?.playableCardIds || adaptedState.playableCardIds.length === 0)) {
        // When a new trick begins, reset stale follow-suit playable cards from the previous trick
        nextPlayableCardIds = [];
      } else if ((adaptedState as any)?.playableCardIds !== undefined && (adaptedState as any)?.playableCardIds !== null) {
        const rawPlayable = (adaptedState as any).playableCardIds;
        const parsedPlayable = Array.isArray(rawPlayable)
          ? rawPlayable
          : (rawPlayable && typeof rawPlayable === 'object' ? Object.values(rawPlayable) : []);
        if (isForMySeat) {
          if (parsedPlayable.length > 0) {
            nextPlayableCardIds = parsedPlayable;
          }
          // If parsedPlayable is empty, preserve existing state.playableCardIds from private hand
        } else {
          // Playable cards from another seat should never contaminate our seat's playable cards
          nextPlayableCardIds = [];
        }
      }
      if (adaptedState) {
        adaptedState.playableCardIds = nextPlayableCardIds;
      }
      return {
        gameState: adaptedState,
        gameConfig: nextConfig,
        playableCardIds: nextPlayableCardIds,
        loading: false,
      };
    });
    triggerRecompute();
  },

  setPlayableCardIds: (rawPlayableCardIds) => {
    const playableCardIds: string[] = Array.isArray(rawPlayableCardIds)
      ? rawPlayableCardIds
      : (rawPlayableCardIds && typeof rawPlayableCardIds === 'object' ? Object.values(rawPlayableCardIds) : []);
    set((state) => {
      const updatedGameState = state.gameState ? { ...state.gameState, playableCardIds } : state.gameState;
      return {
        playableCardIds,
        gameState: updatedGameState,
      };
    });
    triggerRecompute();
  },

  setCanGiveUp: (canGiveUp) => {
    set({ canGiveUp: Boolean(canGiveUp) });
    triggerRecompute();
  },

  setCanClaim: (canClaim) => {
    set({ canClaim: Boolean(canClaim) });
    triggerRecompute();
  },

  setGameConfig: (gameConfig) =>
    set({
      gameConfig: gameConfig ? normalizeGameTableConfig(gameConfig) : null,
    }),

  setAuthoritativeBaseState: (authoritativeBaseState) => set({ authoritativeBaseState }),
  setEffectiveGameState: (effectiveGameState) => set({ effectiveGameState }),
  setMySeat: (mySeat) => {
    set((state) => {
      const currentTurn = (state.gameState as any)?.currentTurnSeat;
      const isNotMyTurn = mySeat === null || (currentTurn !== undefined && currentTurn !== mySeat);
      const playableCardIds = isNotMyTurn ? [] : state.playableCardIds;
      const updatedGameState = state.gameState
        ? { ...state.gameState, playableCardIds }
        : state.gameState;
      return {
        mySeat,
        playableCardIds,
        gameState: updatedGameState,
      };
    });
    triggerRecompute();
  },
  setAuthoritativeMySeat: (authoritativeMySeat) => {
    set({ authoritativeMySeat });
    triggerRecompute();
  },

  setMyPrivateHand: (updater, targetTableId) => {
    set((state) => {
      if (targetTableId && state.tableId && targetTableId !== state.tableId) {
        return state;
      }
      const nextHand = typeof updater === 'function' ? updater(state.myPrivateHand) : updater;
      return {
        myPrivateHand: nextHand,
        privateHandTableId: targetTableId || state.tableId,
      };
    });
    triggerRecompute();
  },

  setEffectivePrivateHand: (effectivePrivateHand) => set({ effectivePrivateHand }),
  setKittyCardIds: (kittyCardIds) => set({ kittyCardIds }),
  setPassedReceivedCardIds: (passedReceivedCardIds) => set({ passedReceivedCardIds }),
  setIsHandVerified: (isHandVerified) => set({ isHandVerified }),
  setIsHandLoading: (isHandLoading) => set({ isHandLoading }),
  setLoading: (loading) => set({ loading }),
});
